import { take, takeLatest, takeEvery, put, call, fork, select, all, delay } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { userSchema } from 'auth/nAmSchemas'

import featureToggles from 'shared/featureToggles'
import { indicateProgress } from 'api'
import { apiLanguageCodeSelector } from 'i18n/selectors'
import { getReservations } from 'grs/actions'
import { STATUS_CONFIRMED, RECEIVED_RESERVATIONS, CANCELED_WAITING_LIST } from 'grs/constants'
import { getRandomness } from 'shared/random-generator'
import { PURCHASE_COMPLETED } from 'basket/constants'
import { clearEntity } from 'entities/actions'

import * as constants from './constants'
import * as selectors from './selectors'
import * as actions from './actions'
import * as grsApi from './grsApi'
import * as nAmApi from './nAmApi'

export function* getToken(action) {
    let response
    let userEmail

    if (!action) {
        const [refreshTokenFromLs, emailFromLs] = yield all([
            yield call([localStorage, localStorage.getItem], 'refreshToken'),
            yield call([localStorage, localStorage.getItem], 'email'),
        ])
        if (!refreshTokenFromLs || !emailFromLs) return

        response = yield call(nAmApi.refreshToken, refreshTokenFromLs)
        userEmail = emailFromLs
    } else {
        const { email, password, translate } = action
        response = yield call(nAmApi.login, email, password, translate)
        userEmail = email
    }

    const { accessToken, refreshToken, expiresIn } = response
    if (!accessToken || !refreshToken || !expiresIn) return

    yield all([
        yield call([localStorage, localStorage.setItem], 'refreshToken', refreshToken),
        yield call([localStorage, localStorage.setItem], 'email', userEmail),
        yield put(
            actions.setToken({
                entities: {
                    users: {
                        [userEmail]: {
                            token: accessToken,
                        },
                    },
                },
                result: userEmail,
                expiresIn: (expiresIn - 60) * 1000,
            })
        ),
    ])
}

export function* getCurrentUserSaga() {
    try {
        let user

        if (featureToggles.newAuthFlow) {
            const email = yield select(selectors.userIdSelector)
            const token = yield select(selectors.tokenSelector)
            if (!email || !token) return

            const grsUserData = yield call(nAmApi.authenticateGrsAccount, token)
            const nAmApiUserData = yield call(indicateProgress, ['getCurrentUser'], nAmApi.getCurrentUser, email, token)
            const nAmUser = nAmApiUserData[0]
            const paymentMethods = nAmUser?.paymentMethods
                ? nAmUser.paymentMethods.filter(
                      paymentMethod => paymentMethod.paymentMethodType === constants.PAYMENT_METHOD_TYPE_CREDIT_CARD
                  )
                : []
            const padMethods = nAmUser?.paymentMethods
                ? nAmUser.paymentMethods.filter(
                      paymentMethod => paymentMethod.paymentMethodType === constants.PAYMENT_METHOD_TYPE_PAD
                  )
                : []

            user = normalize({ ...grsUserData, ...nAmUser, paymentMethods, token, padMethods }, userSchema)
        } else {
            user = yield call(indicateProgress, ['getCurrentUser'], grsApi.getCurrentUser)
        }
        yield put(actions.setAuthenticatedUser(user))
    } catch (error) {
        yield put(actions.logout())
    }
}

function* updateUserCardListSaga() {
    yield takeLatest(constants.SET_AUTHENTICATED_USER, function* () {
        const user = yield select(selectors.userSelector)
        if (user && user.creditCards.length === 0) {
            yield put(clearEntity('creditCards'))
        }
    })
}

function* restoreUserSessionSaga() {
    yield takeLatest(constants.RESTORE_USER_SESSION, function* () {
        try {
            yield indicateProgress(['restoreUserSession'], function* () {
                if (featureToggles.newAuthFlow) {
                    yield getToken()
                    yield getCurrentUserSaga()
                } else {
                    yield getCurrentUserSaga()
                }
            })
        } catch (error) {}
    })

    // called once by itself on the first load
    yield put(actions.restoreUserSession())
}

export function* loginSaga() {
    yield takeLatest(constants.LOGIN, function* ({ email, password, remember, translate }) {
        try {
            yield indicateProgress(['login'], function* () {
                if (featureToggles.newAuthFlow) {
                    yield getToken({ email, password, translate })
                    yield getCurrentUserSaga()
                } else {
                    const apiLanguageCode = yield select(apiLanguageCodeSelector)
                    const user = yield call(grsApi.login, email, password, remember, apiLanguageCode)
                    yield put(actions.setAuthenticatedUser(user))
                }
            })
        } catch (error) {}
    })
}

function* logoutSaga() {
    yield takeLatest(constants.LOGOUT, function* () {
        try {
            if (featureToggles.newAuthFlow) {
                yield all([
                    yield call([localStorage, localStorage.removeItem], 'refreshToken'),
                    yield call([localStorage, localStorage.removeItem], 'email'),
                ])
                // todo: add logout call
            } else {
                yield call(grsApi.logout)
            }
            yield put(actions.setAuthenticatedUser())
        } catch (error) {}
    })
}

export function* watchRefreshAuthenticatedUserSaga() {
    yield takeLatest(constants.REFRESH_AUTHENTICATED_USER, function* () {
        yield getCurrentUserSaga()
    })
}

function* createAccountSaga() {
    yield takeLatest(
        constants.CREATE_ACCOUNT,
        function* ({
            firstName,
            lastName,
            email,
            password,
            nif,
            country,
            customerType,
            customerRegNr,
            businessName,
            phone,
        }) {
            try {
                yield indicateProgress(['createAccount'], function* () {
                    if (featureToggles.newAuthFlow) {
                        const response = yield call(nAmApi.checkUsernameAvailability, email)

                        if (response && response.count > 0) {
                            throw new Error(constants.CREATE_ACCOUNT_USER_EXISTS)
                        } else {
                            yield call(nAmApi.createAccount, firstName, lastName, email, password)
                            yield getToken({ email, password })
                            yield getCurrentUserSaga()
                        }
                    } else {
                        const apiLanguageCode = yield select(apiLanguageCodeSelector)
                        let formattedCustomerRegNr = customerRegNr

                        if (customerRegNr) {
                            formattedCustomerRegNr = customerRegNr.replace(/,|\.|-|\/|\\/g, '')
                        }

                        const response = yield call(
                            grsApi.createAccount,
                            firstName,
                            lastName,
                            email,
                            password,
                            nif,
                            apiLanguageCode,
                            country,
                            customerType,
                            formattedCustomerRegNr,
                            businessName,
                            phone
                        )

                        if (!featureToggles.brNeoPurchaseFlow) {
                            yield put(actions.setAuthenticatedUser(response))
                        }
                    }
                })
            } catch (error) {}
        }
    )
}

export function* resetPasswordSaga() {
    while (true) {
        let response

        while (!response) {
            const { email } = yield take(constants.RESET_PASSWORD)

            try {
                const apiLanguageCode = yield select(apiLanguageCodeSelector)

                response = yield call(indicateProgress, ['resetPassword'], grsApi.resetPassword, email, apiLanguageCode)
            } catch (error) {}
        }
    }
}

export function* waitingListNotificationSaga() {
    yield takeEvery([constants.SET_AUTHENTICATED_USER, CANCELED_WAITING_LIST, PURCHASE_COMPLETED], function* () {
        const requestId = getRandomness()
        yield put(getReservations(0, 1, STATUS_CONFIRMED, requestId))

        const { result } = yield take(action => action.type === RECEIVED_RESERVATIONS && action.requestId === requestId)
        yield put(actions.setLatestConfirmedWaitingList(result.length > 0 ? result[0] : null))
    })
}

export function* refreshTokenSaga() {
    yield takeLatest(constants.SET_TOKEN, function* ({ expiresIn }) {
        try {
            yield delay(expiresIn)
            yield getToken()
        } catch (error) {
            yield put(actions.logout())
        }
    })
}

export default function* rootSaga() {
    yield fork(loginSaga)
    yield fork(logoutSaga)
    yield fork(watchRefreshAuthenticatedUserSaga)
    yield fork(createAccountSaga)
    yield fork(resetPasswordSaga)
    yield fork(waitingListNotificationSaga)
    yield fork(restoreUserSessionSaga)
    yield fork(refreshTokenSaga)
    yield fork(updateUserCardListSaga)
}
